import React from "react";
import { Grid, Segment } from "semantic-ui-react";
import _ from "lodash";
import currency from "currency.js";
import styles from "./styles.module.scss";
import { CFItem } from "../../types";

export interface Props {
  cfReport: CFItem;
  viewProjected: boolean;
}

const ClearingFundDetail = ({ cfReport, viewProjected }: Props) => {
  const cfResultIsEmpty = Object.keys(cfReport).length === 0;
  const cfProjected = cfReport.projected;
  const detail = cfReport.detail;
  const total = cfReport.total;

  const totalProjPath = viewProjected
    ? "apex-total.total.current"
    : "total.total.current";

  const excessProjPath = viewProjected
    ? "apex-total.excess.current"
    : "total.excess.current";

  const varProjPath = viewProjected
    ? "apex-var.var-requirement-amount.current"
    : "var.var-requirement-amount.current";

  function displayDetail(
    element: any,
    projectedPath: string,
    currentPath: string
  ) {
    if (!cfResultIsEmpty) {
      return cfReport.hasOwnProperty("projected") ? (
        <span className={styles.currency}>
          {currency(_.get(cfProjected, projectedPath, 0)).format()}
          <b className={styles.text}> (Forecasted)</b>
        </span>
      ) : (
        <span className={styles.currency}>
          {currency(_.get(element, currentPath, 0)).format()}
        </span>
      );
    } else {
      return " -";
    }
  }

  function getDetailSegment(element: any, path: string) {
    return !cfResultIsEmpty ? currency(_.get(element, path, 0)).format() : " -";
  }

  return (
    <Grid id={"cf-detail"} columns={2} divided padded>
      <Grid.Row>
        <Grid.Column
          id={viewProjected ? "projected-cf-detail-col1" : "cf-detail-col1"}
          className={viewProjected ? styles.projectedView : ""}
        >
          <Segment.Group raised>
            <Segment id="total-req">
              <b style={{ fontWeight: "bold" }}>Total Requirement:</b>
              {displayDetail(total, totalProjPath, "total.current")}
            </Segment>
            <Segment id="excess">
              <b style={{ fontWeight: "bold" }}>Excess:</b>
              {displayDetail(total, excessProjPath, "excess.current")}
            </Segment>
            <Segment id="mtm">
              <span style={{ fontWeight: "bold" }}>MTM:</span>
              {displayDetail(
                detail,
                "mtm.mtm-requirement-amount.current",
                "mtm-requirement-amount.current"
              )}
            </Segment>
            <Segment id="volatility">
              <span style={{ fontWeight: "bold" }}>VaR:</span>
              {displayDetail(
                detail,
                varProjPath,
                "var-requirement-amount.current"
              )}
            </Segment>
            <Segment id="wi-mtm">
              <span style={{ fontWeight: "bold" }}>WI MTM:</span>
              <span className={styles.currency}>
                {getDetailSegment(detail, "wi-mtm-requirement-amount.current")}
              </span>
            </Segment>
            <Segment id="cns-fails">
              <span style={{ fontWeight: "bold" }}>CNS Fails:</span>
              <span className={styles.currency}>
                {getDetailSegment(
                  detail,
                  "cns-fails-requirement-amount.current"
                )}
              </span>
            </Segment>
            <Segment id="non-cns">
              <span style={{ fontWeight: "bold" }}>Non-CNS:</span>
              <span className={styles.currency}>
                {getDetailSegment(detail, "non-cns-requirement-amount.current")}
              </span>
            </Segment>
          </Segment.Group>
        </Grid.Column>
        <Grid.Column>
          <Segment.Group raised>
            <Segment id="fund-serv">
              <span style={{ fontWeight: "bold" }}>Fund/Serv:</span>
              <span className={styles.currency}>
                {getDetailSegment(
                  detail,
                  "fund-serv-requirement-amount.current"
                )}
              </span>
            </Segment>
            <Segment id="illiquid">
              <span style={{ fontWeight: "bold" }}>Illiquid:</span>
              <span className={styles.currency}>
                {getDetailSegment(
                  detail,
                  "illiquid-requirement-amount.current"
                )}
              </span>
            </Segment>
            <Segment id="cur-dep">
              <span style={{ fontWeight: "bold" }}>Current Deposit:</span>
              <span className={styles.currency}>
                {getDetailSegment(total, "deposit.current")}
              </span>
            </Segment>
            <Segment id="spc-charge">
              <span style={{ fontWeight: "bold" }}>SPC:</span>
              <span className={styles.currency}>
                {getDetailSegment(detail, "spc-requirement-amount.current")}
              </span>
            </Segment>
            <Segment id="mrd">
              <span style={{ fontWeight: "bold" }}>MRD:</span>
              <span className={styles.currency}>
                {getDetailSegment(detail, "mrd-requirement-amount.current")}
              </span>
            </Segment>
            <Segment id="coverage-charge">
              <span style={{ fontWeight: "bold" }}>Coverage:</span>
              <span className={styles.currency}>
                {getDetailSegment(
                  detail,
                  "coverage-requirement-amount.current"
                )}
              </span>
            </Segment>
            <Segment id="min-charge">
              <span style={{ fontWeight: "bold" }}>Minimum Charge Amount:</span>
              <span className={styles.currency}>
                {getDetailSegment(detail, "minimum-charge-amount.current")}
              </span>
            </Segment>
          </Segment.Group>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};
export default ClearingFundDetail;
